/**
 * Meta standard-event mapping, event_id helpers, and CAPI send gates.
 * Pure module — safe to unit test. I/O lives in metaConversions.server.ts.
 */

export const META_STANDARD_EVENTS = {
  ViewContent: 'ViewContent',
  Lead: 'Lead',
  CompleteRegistration: 'CompleteRegistration',
  InitiateCheckout: 'InitiateCheckout',
  Purchase: 'Purchase',
} as const;

export type MetaStandardEventName =
  (typeof META_STANDARD_EVENTS)[keyof typeof META_STANDARD_EVENTS];

/** PostHog event → Meta standard event. Purchase stays on the payment callback path. */
export const POSTHOG_TO_META_EVENT: Record<string, MetaStandardEventName> = {
  view_pricing_section: 'ViewContent',
  view_start_page: 'ViewContent',
  exit_intent_email_submitted: 'Lead',
  submit_waitlist_form: 'Lead',
  waitlist_submission_success: 'Lead',
  submit_registration_form: 'CompleteRegistration',
  initiate_payment: 'InitiateCheckout',
  payment_success: 'Purchase',
};

export const CLIENT_CAPI_EVENT_NAMES: readonly MetaStandardEventName[] = [
  'ViewContent',
  'Lead',
  'CompleteRegistration',
  'InitiateCheckout',
];

export type MetaCapiSkipReason =
  | 'capi_disabled'
  | 'not_production'
  | 'vercel_preview'
  | 'missing_pixel_id'
  | 'missing_access_token'
  | 'consent_not_granted'
  | 'missing_event_id'
  | 'invalid_value'
  | 'explicit_test_checkout';

export interface MetaCapiEnv {
  nodeEnv?: string;
  vercelEnv?: string;
  pixelId?: string;
  accessToken?: string;
  /**
   * Dedicated CAPI switch (`META_CAPI_ENABLED`).
   * - unset: send in production
   * - 'true': also send from preview/dev (Test Events)
   * - 'false': never send
   */
  enabled?: string;
  /** Alias for enabled === 'true'. Prefer META_CAPI_ENABLED. */
  allowNonProd?: boolean;
  /** Events Manager test_event_code. When set, also allows non-prod sends. */
  testEventCode?: string;
  /**
   * Ignored for CAPI. YAADPAY_SANDBOX_MODE is a payment workaround
   * (empty Hyp IP allow-list) and is true in production on purpose.
   */
  yaadpaySandboxMode?: string;
}

export interface MetaEventIdInput {
  eventName: MetaStandardEventName;
  transactionId?: string;
  randomId?: string;
}

export function mapPostHogEventToMeta(eventName: string): MetaStandardEventName | null {
  return POSTHOG_TO_META_EVENT[eventName] ?? null;
}

export function createMetaEventId(input: MetaEventIdInput): string {
  if (input.transactionId?.trim()) {
    if (input.eventName === 'Purchase' || input.eventName === 'InitiateCheckout') {
      return input.transactionId.trim();
    }
    return `${input.eventName}:${input.transactionId.trim()}`;
  }
  return input.randomId?.trim() || `${input.eventName}:${Date.now()}`;
}

/** FNV-1a over UTF-16 code units with two seeds → 16 hex chars. Sync on client and server. */
function fnv1aHex(value: string): string {
  let a = 0x811c9dc5;
  let b = 0x050c5d1f;
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    a = Math.imul(a ^ code, 0x01000193);
    b = Math.imul(b ^ code, 0x01000193);
  }
  return (a >>> 0).toString(16).padStart(8, '0') + (b >>> 0).toString(16).padStart(8, '0');
}

/**
 * Stable per-email event_id (e.g. `Lead:exit:<hash>`). The Pixel and both
 * CAPI senders compute the same id, so Meta and the Firestore audit doc
 * dedupe without a raw email in `event_id` or in a doc id.
 */
export function metaEmailEventId(
  eventName: MetaStandardEventName,
  scope: string,
  email: string
): string {
  try {
    const normalized = typeof email === 'string' ? email.trim().toLowerCase() : '';
    return `${eventName}:${scope}:${fnv1aHex(normalized)}`;
  } catch {
    return `${eventName}:${scope}:0`;
  }
}

/**
 * Firestore audit id. Must include the event name so InitiateCheckout and
 * Purchase can share Meta `event_id` (transactionId) without blocking each other.
 */
export function metaCapiAuditDocId(
  eventName: MetaStandardEventName,
  eventId: string
): string {
  return `${eventName}:${eventId}`;
}

/** ViewContent ids are per view; auditing them in Firestore dedupes nothing. */
export function shouldAuditMetaEvent(eventName: MetaStandardEventName): boolean {
  return eventName !== 'ViewContent';
}

export function isClientCapiEvent(eventName: string): eventName is MetaStandardEventName {
  return (CLIENT_CAPI_EVENT_NAMES as readonly string[]).includes(eventName);
}

function isProductionRuntime(env: MetaCapiEnv): boolean {
  const nodeOk = env.nodeEnv === 'production';
  const vercelOk = !env.vercelEnv || env.vercelEnv === 'production';
  return nodeOk && vercelOk;
}

/** Preview/dev may send when a dedicated tracking flag or test code is set. */
export function hasMetaCapiTestOverride(env: MetaCapiEnv): boolean {
  return (
    env.enabled === 'true' ||
    env.allowNonProd === true ||
    Boolean(env.testEventCode?.trim())
  );
}

/**
 * CAPI is gated on production runtime + pixel/token, or a dedicated test
 * flag (`META_CAPI_ENABLED=true` / `META_CAPI_TEST_EVENT_CODE`).
 *
 * Never consult YAADPAY_SANDBOX_MODE. That env is true in production on
 * purpose (Hyp IP allow-list workaround) and must not silence ads tracking.
 */
export function getMetaCapiEnvironmentSkipReasons(env: MetaCapiEnv): MetaCapiSkipReason[] {
  const reasons: MetaCapiSkipReason[] = [];
  if (env.enabled === 'false') {
    reasons.push('capi_disabled');
  } else if (!isProductionRuntime(env) && !hasMetaCapiTestOverride(env)) {
    if (env.nodeEnv !== 'production') reasons.push('not_production');
    if (env.vercelEnv && env.vercelEnv !== 'production') {
      reasons.push('vercel_preview');
    }
  }
  if (!env.pixelId?.trim()) reasons.push('missing_pixel_id');
  if (!env.accessToken?.trim()) reasons.push('missing_access_token');
  return reasons;
}

export function hasStoredMetaConsent(tracking: { consent?: string } | null | undefined): boolean {
  return tracking?.consent === 'granted';
}

export function getMetaCapiEventSkipReasons(params: {
  env: MetaCapiEnv;
  consentGranted: boolean;
  eventId?: string;
  value?: number;
  requirePositiveValue?: boolean;
  isExplicitTestCheckout?: boolean;
}): MetaCapiSkipReason[] {
  const reasons = getMetaCapiEnvironmentSkipReasons(params.env);
  if (!params.consentGranted) reasons.push('consent_not_granted');
  if (!params.eventId?.trim()) reasons.push('missing_event_id');
  if (params.isExplicitTestCheckout) reasons.push('explicit_test_checkout');
  if (params.requirePositiveValue) {
    if (!Number.isFinite(params.value) || (params.value ?? 0) <= 0) {
      reasons.push('invalid_value');
    }
  }
  return reasons;
}

export function readMetaCapiEnvFromProcess(
  env: NodeJS.ProcessEnv = process.env
): MetaCapiEnv {
  return {
    nodeEnv: env.NODE_ENV,
    vercelEnv: env.VERCEL_ENV,
    pixelId: env.NEXT_PUBLIC_META_PIXEL_ID,
    accessToken: env.META_CONVERSIONS_API_TOKEN,
    enabled: env.META_CAPI_ENABLED,
    allowNonProd: env.META_CAPI_ALLOW_NON_PROD === 'true',
    testEventCode: env.META_CAPI_TEST_EVENT_CODE,
    yaadpaySandboxMode: env.YAADPAY_SANDBOX_MODE,
  };
}
